import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, VolumeX } from 'lucide-react';
import { MUTE_DURATIONS, type MuteType } from './hooks/use-chat-moderation';

interface MuteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  username: string;
  userId: string;
  onMute: (muteType: MuteType, reason?: string) => Promise<void>;
  isMuting: boolean;
}

export function MuteUserDialog({
  open,
  onOpenChange,
  username,
  userId,
  onMute,
  isMuting,
}: MuteUserDialogProps) {
  const [muteType, setMuteType] = useState<MuteType>('quick');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      await onMute(muteType, reason.trim() || undefined);
      // Reset form and close
      setMuteType('quick');
      setReason('');
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to mute user');
    }
  };

  const handleClose = () => {
    setMuteType('quick');
    setReason('');
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <VolumeX className="h-5 w-5 text-orange-500" />
            Mute User
          </DialogTitle>
          <DialogDescription>
            Mute <strong>{username}</strong> from sending messages in this league's chat.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Duration selection */}
          <div className="grid gap-2">
            <Label>Mute Duration</Label>
            <RadioGroup
              value={muteType}
              onValueChange={(value) => setMuteType(value as MuteType)}
              className="grid gap-2"
            >
              {Object.entries(MUTE_DURATIONS).map(([value, { label }]) => (
                <div key={value} className="flex items-center space-x-2">
                  <RadioGroupItem value={value} id={`mute-${value}`} />
                  <Label htmlFor={`mute-${value}`} className="font-normal cursor-pointer">
                    {label}
                    {value === 'quick' && (
                      <span className="text-xs text-muted-foreground ml-1">
                        (cool-down)
                      </span>
                    )}
                    {value === 'permanent' && (
                      <span className="text-xs text-orange-500 ml-1">
                        (requires manual unmute)
                      </span>
                    )}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Reason */}
          <div className="grid gap-2">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you muting this user?"
              rows={2}
              maxLength={200}
            />
          </div>

          {/* Warning for permanent */}
          {muteType === 'permanent' && (
            <div className="text-sm text-orange-600 bg-orange-500/10 px-3 py-2 rounded-lg">
              Permanent mutes will last until manually unmuted. Consider a shorter
              duration first for minor issues.
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isMuting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isMuting} variant="destructive">
            {isMuting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Muting...
              </>
            ) : (
              <>
                <VolumeX className="h-4 w-4 mr-2" />
                Mute User
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
